import { useContext, useEffect, useState } from "react"
import ReactModal from "react-modal"
import { BrukerContext } from "../BrukerContext"
import supabase from "../supabaseconfig"
import { Recommendation } from "../types/supabase"
import style from "./anbefalinger.module.css"
import { AnbefalingType, visningstekstForKategori } from "./utils"

async function uploadPhotos(files: File[]): Promise<string[]> {
    const paths: string[] = []
    for (const file of files) {
        const filnavn = `${Date.now()}-${file.name}`
        const { error } = await supabase()
            .storage.from("recommendation-photos")
            .upload(filnavn, file)

        if (error) {
            console.error("Feil ved opplasting av bilde:", error)
            continue
        }

        paths.push(filnavn)
    }
    return paths
}

export function LeggTilAnbefalingModal({
    vis,
    lukk,
    eksisterende,
    onLagret,
}: {
    vis: boolean
    lukk: () => void
    eksisterende?: Recommendation
    onLagret?: () => void
}) {
    const { bruker } = useContext(BrukerContext)
    const erRedigering = !!eksisterende
    const [type, setType] = useState<AnbefalingType | undefined>()
    const [feilmelding, setFeilmeding] = useState<string | undefined>()
    const [visSuksess, setVisSuksess] = useState(false)
    const [laster, setLaster] = useState(false)
    const [selectedFiles, setSelectedFiles] = useState<File[]>([])
    const [eksisterendeBilder, setEksisterendeBilder] = useState<string[]>([])
    const [fjernetBilder, setFjernetBilder] = useState<string[]>([])
    const [eksisterendeSignedUrls, setEksisterendeSignedUrls] = useState<Record<string, string>>({})
    const [anbefaling, setAnbefaling] = useState<Recommendation>({
        user: bruker,
    })

    useEffect(() => {
        if (eksisterende && vis) {
            setAnbefaling(eksisterende)
            setType(toEnum(eksisterende.type))
            setEksisterendeBilder(eksisterende.photo_urls ?? [])
            setFjernetBilder([])
            setSelectedFiles([])
            setVisSuksess(false)
            setFeilmeding(undefined)
        } else if (!vis) {
            setAnbefaling({ user: bruker })
            setType(undefined)
            setEksisterendeBilder([])
            setFjernetBilder([])
            setSelectedFiles([])
            setEksisterendeSignedUrls({})
            setVisSuksess(false)
            setFeilmeding(undefined)
        }
    }, [eksisterende, vis, bruker])

    useEffect(() => {
        if (!vis || eksisterendeBilder.length === 0) {
            setEksisterendeSignedUrls({})
            return
        }
        async function hentUrler() {
            const { data, error } = await supabase()
                .storage.from("recommendation-photos")
                .createSignedUrls(eksisterendeBilder, 3600)
            if (error) {
                console.error("Feil ved henting av signerte URLer:", error)
                return
            }
            const urlMap: Record<string, string> = {}
            data.forEach((item, i) => {
                urlMap[eksisterendeBilder[i]] = item.signedUrl
            })
            setEksisterendeSignedUrls(urlMap)
        }
        hentUrler()
    }, [vis, eksisterendeBilder])

    function validerInput(): boolean {
        //TODO Valider
        return true
    }

    async function lagreAnbefaling() {
        if (validerInput()) {
            setLaster(true)

            let photoUrls: string[] = []
            if (selectedFiles.length > 0) {
                photoUrls = await uploadPhotos(selectedFiles)
            }

            if (erRedigering) {
                // Delete removed photos from storage
                if (fjernetBilder.length > 0) {
                    const { error: storageError } = await supabase()
                        .storage.from("recommendation-photos")
                        .remove(fjernetBilder)
                    if (storageError) {
                        console.error("Feil ved sletting av bilder:", storageError)
                    }
                }

                const beholdteBilder = eksisterendeBilder.filter(
                    (b) => !fjernetBilder.includes(b),
                )
                const alleBilder = [...beholdteBilder, ...photoUrls]

                const { error } = await supabase()
                    .from("recommendations")
                    .update({
                        name: anbefaling.name,
                        description: anbefaling.description,
                        type: anbefaling.type,
                        rating: anbefaling.rating ?? null,
                        address: anbefaling.address ?? null,
                        url: anbefaling.url ?? null,
                        photo_urls: alleBilder.length > 0 ? alleBilder : null,
                    })
                    .eq("id", eksisterende!.id!)

                if (error) {
                    console.error("Feil ved oppdatering av anbefaling:", error)
                    setFeilmeding("En feil skjedde under lagring av anbefaling.")
                } else {
                    setVisSuksess(true)
                    onLagret?.()
                }
            } else {
                let { error } = await supabase()
                    .from("recommendations")
                    .insert({
                        ...anbefaling,
                        photo_urls: photoUrls.length > 0 ? photoUrls : undefined,
                    })

                if (error) {
                    console.error("Feil ved opplasting av anbefaling")
                    setFeilmeding("En feil skjedde under lagring av anbefaling.")
                } else {
                    setVisSuksess(true)
                    onLagret?.()
                }
            }
            setLaster(false)
        } else {
            setFeilmeding("Du må fylle ut manglende felter")
        }
    }

    const visInnhold = !laster && !feilmelding && !visSuksess

    function harStartetUtfylling(): boolean {
        if (erRedigering) return false
        return (
            !!type ||
            !!anbefaling.name ||
            !!anbefaling.description ||
            !!anbefaling.rating ||
            !!anbefaling.address ||
            !!anbefaling.url ||
            selectedFiles.length > 0
        )
    }

    function handleLukk() {
        if (harStartetUtfylling()) {
            if (!window.confirm("Du har begynt å fylle ut skjemaet. Er du sikker på at du vil lukke?")) {
                return
            }
        }
        lukk()
    }

    function fjernEksisterendeBilde(path: string) {
        setFjernetBilder((prev) => [...prev, path])
    }

    return (
        <ReactModal
            ariaHideApp={false}
            isOpen={vis}
            onRequestClose={handleLukk}
            shouldCloseOnOverlayClick={true}
            style={{
                overlay: { backgroundColor: "rgba(0, 0, 0, 0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 20 },
                content: { position: "relative", inset: "auto", border: "none", background: "none", padding: 0, maxWidth: "520px", width: "90%" },
            }}
        >
            <div className={style.modal}>
                {laster && <h2>Laster...</h2>}
                {feilmelding && <h2>{feilmelding}</h2>}
                {visSuksess && (
                    <>
                        <h2>{erRedigering ? "Anbefalingen er oppdatert! 🎉" : "Din anbefaling er lagret! 🎉"}</h2>
                        <button onClick={lukk}>Lukk</button>
                    </>
                )}
                {visInnhold && (
                    <>
                        <h2>{erRedigering ? "Rediger anbefaling" : "Legg til ny anbefaling"}</h2>

                        <label className={style.inputPar}>
                            Hva skal du legge inn?
                            <select
                                value={type ?? ""}
                                onChange={(event) => {
                                    setAnbefaling({
                                        ...anbefaling,
                                        type: event.target.value,
                                    })
                                    setType(toEnum(event.target.value))
                                }}
                            >
                                <option value={undefined}></option>
                                {Object.keys(AnbefalingType).map((it) => (
                                    <option key={it} value={it}>
                                        {visningstekstForKategori(
                                            AnbefalingType[it as keyof typeof AnbefalingType],
                                        )}
                                    </option>
                                ))}
                            </select>
                        </label>

                        {type && (
                            <NyAnbefalingSkjema
                                type={type}
                                anbefaling={anbefaling}
                                setAnbefaling={setAnbefaling}
                                selectedFiles={selectedFiles}
                                setSelectedFiles={setSelectedFiles}
                                eksisterendeSignedUrls={eksisterendeSignedUrls}
                                eksisterendeBilder={eksisterendeBilder}
                                fjernetBilder={fjernetBilder}
                                onFjernEksisterendeBilde={fjernEksisterendeBilde}
                            />
                        )}

                        <div className={style.knapper}>
                            <button onClick={handleLukk}>Avbryt</button>
                            <button onClick={() => lagreAnbefaling()}>
                                Lagre
                            </button>
                        </div>
                    </>
                )}
            </div>
        </ReactModal>
    )
}

function NyAnbefalingSkjema({
    type,
    anbefaling,
    setAnbefaling,
    selectedFiles,
    setSelectedFiles,
    eksisterendeSignedUrls,
    eksisterendeBilder,
    fjernetBilder,
    onFjernEksisterendeBilde,
}: {
    type: AnbefalingType
    anbefaling: Recommendation
    setAnbefaling: (r: Recommendation) => void
    selectedFiles: File[]
    setSelectedFiles: (files: File[]) => void
    eksisterendeSignedUrls: Record<string, string>
    eksisterendeBilder: string[]
    fjernetBilder: string[]
    onFjernEksisterendeBilde: (path: string) => void
}) {
    const [ratingFeil, setRatingFeil] = useState<string | undefined>()

    const synligeBilder = eksisterendeBilder
        .filter((path) => !fjernetBilder.includes(path))
        .map((path) => ({ path, url: eksisterendeSignedUrls[path] }))

    return (
        <>
            <label className={style.inputPar}>
                Navn på {type.toLocaleLowerCase()}
                <input
                    type="text"
                    value={anbefaling.name ?? ""}
                    onChange={(event) =>
                        setAnbefaling({
                            ...anbefaling,
                            name: event.target.value,
                        })
                    }
                />
            </label>

            <label className={style.inputPar}>
                Beskrivelse
                <textarea
                    value={anbefaling.description ?? ""}
                    onChange={(event) =>
                        setAnbefaling({
                            ...anbefaling,
                            description: event.target.value,
                        })
                    }
                />
            </label>

            <label className={style.inputPar}>
                Terningkast ⭐️
                <input
                    type="number"
                    min={1}
                    max={6}
                    value={anbefaling.rating ?? ""}
                    onChange={(event) => {
                        const val = event.target.value
                        if (val === "") {
                            setRatingFeil(undefined)
                            setAnbefaling({ ...anbefaling, rating: undefined })
                            return
                        }
                        const num = Number(val)
                        if (num < 1 || num > 6 || !Number.isInteger(num)) {
                            setRatingFeil("Terningkast må være mellom 1 og 6")
                            setAnbefaling({ ...anbefaling, rating: undefined })
                        } else {
                            setRatingFeil(undefined)
                            setAnbefaling({ ...anbefaling, rating: num })
                        }
                    }}
                />
                {ratingFeil && <span className={style.feilmelding}>{ratingFeil}</span>}
            </label>

            <label className={style.inputPar}>
                Adresse
                <input
                    type="text"
                    value={anbefaling.address ?? ""}
                    onChange={(event) =>
                        setAnbefaling({
                            ...anbefaling,
                            address: event.target.value,
                        })
                    }
                />
            </label>

            <label className={style.inputPar}>
                Nettside
                <input
                    type="text"
                    value={anbefaling.url ?? ""}
                    onChange={(event) =>
                        setAnbefaling({
                            ...anbefaling,
                            url: event.target.value,
                        })
                    }
                />
            </label>

            <div className={style.inputPar}>
                Bilder
                <label className={style.bildeOpplasting}>
                    <input
                        type="file"
                        multiple
                        accept="image/*"
                        onChange={(event) => {
                            if (event.target.files) {
                                setSelectedFiles(Array.from(event.target.files))
                            }
                        }}
                    />
                    <span className={style.bildeOpplastingTekst}>
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M17 13v2a2 2 0 01-2 2H5a2 2 0 01-2-2v-2M14 7l-4-4M10 3v10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        {selectedFiles.length > 0
                            ? `${selectedFiles.length} nytt bilde${selectedFiles.length > 1 ? "r" : ""} valgt`
                            : "Velg bilder"}
                    </span>
                </label>
            </div>

            {synligeBilder.length > 0 && (
                <div className={style.bildeForhandsvisning}>
                    {synligeBilder.map(({ path, url }) =>
                        url ? (
                            <div key={path} className={style.thumbnailWrapper}>
                                <img
                                    src={url}
                                    alt="Eksisterende bilde"
                                    className={style.thumbnail}
                                />
                                <button
                                    type="button"
                                    className={style.fjernBildeKnapp}
                                    onClick={() => onFjernEksisterendeBilde(path)}
                                    title="Fjern bilde"
                                >
                                    ✕
                                </button>
                            </div>
                        ) : null,
                    )}
                </div>
            )}

            {selectedFiles.length > 0 && (
                <div className={style.bildeForhandsvisning}>
                    {selectedFiles.map((file, index) => (
                        <img
                            key={index}
                            src={URL.createObjectURL(file)}
                            alt={file.name}
                            className={style.thumbnail}
                        />
                    ))}
                </div>
            )}
        </>
    )
}

export function toEnum(s?: string): AnbefalingType | undefined {
    return s ? AnbefalingType[s as keyof typeof AnbefalingType] : undefined
}
